import { TagGroupSelectionMode, TagScope } from '@vivport/backend/types';
import { Save } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';

export interface TagGroupFormValues {
  name: string;
  description: string;
  scope: TagScope;
  selectionMode: TagGroupSelectionMode;
  groupOrder?: number;
}

interface Work4MeTagGroupFormProps {
  initialValues: TagGroupFormValues;
  isSubmitting: boolean;
  onSubmit: (values: TagGroupFormValues) => Promise<void>;
  submitLabel: string;
  submittingLabel: string;
  showScopeSelector?: boolean;
}

export function Work4MeTagGroupForm({
  initialValues,
  isSubmitting,
  onSubmit,
  submitLabel,
  submittingLabel,
  showScopeSelector = true,
}: Work4MeTagGroupFormProps) {
  const [groupName, setGroupName] = useState(initialValues.name);
  const [description, setDescription] = useState(initialValues.description);
  const [scope, setScope] = useState<TagScope>(initialValues.scope);
  const [selectionMode, setSelectionMode] = useState<TagGroupSelectionMode>(initialValues.selectionMode);
  const [groupOrder, setGroupOrder] = useState(initialValues.groupOrder === undefined ? '' : String(initialValues.groupOrder));

  useEffect(() => {
    setGroupName(initialValues.name);
    setDescription(initialValues.description);
    setScope(initialValues.scope);
    setSelectionMode(initialValues.selectionMode);
    setGroupOrder(initialValues.groupOrder === undefined ? '' : String(initialValues.groupOrder));
  }, [initialValues]);

  const parsedGroupOrder = useMemo(() => {
    if (groupOrder.trim() === '') return undefined;
    const parsed = Number(groupOrder);
    return Number.isInteger(parsed) ? parsed : undefined;
  }, [groupOrder]);

  const isFormValid = groupName.trim().length > 0;

  const handleSubmit = async () => {
    if (!isFormValid || isSubmitting) {
      return;
    }

    await onSubmit({
      name: groupName.trim(),
      description: description.trim(),
      scope,
      selectionMode,
      groupOrder: parsedGroupOrder,
    });
  };

  return (
    <div className="space-y-5">
      <div>
        <Label htmlFor="group-name">Group Name *</Label>
        <Input
          id="group-name"
          placeholder="e.g., Status"
          value={groupName}
          onChange={(event) => setGroupName(event.target.value)}
          className="mt-1"
        />
      </div>

      <div>
        <Label htmlFor="group-description">Description</Label>
        <Textarea
          id="group-description"
          placeholder="Optional description..."
          rows={3}
          value={description}
          onChange={(event) => setDescription(event.target.value)}
          className="mt-1 resize-none"
        />
      </div>

      {showScopeSelector ? (
        <div>
          <Label htmlFor="group-scope">Scope</Label>
          <Select value={scope} onValueChange={(value) => setScope(value as TagScope)}>
            <SelectTrigger className="mt-1">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="PRIVATE">PRIVATE</SelectItem>
              <SelectItem value="HUB">HUB</SelectItem>
            </SelectContent>
          </Select>
        </div>
      ) : null}

      <div>
        <Label htmlFor="group-selection-mode">Selection Mode</Label>
        <Select value={selectionMode} onValueChange={(value) => setSelectionMode(value as TagGroupSelectionMode)}>
          <SelectTrigger className="mt-1">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="SINGLE">SINGLE</SelectItem>
            <SelectItem value="MULTI">MULTI</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label htmlFor="group-order">Order</Label>
        <Input
          id="group-order"
          value={groupOrder}
          onChange={(event) => setGroupOrder(event.target.value)}
          placeholder="Optional integer"
          className="mt-1"
        />
      </div>

      <Button
        onClick={handleSubmit}
        className="w-full gap-2"
        disabled={!isFormValid || isSubmitting}
      >
        {isSubmitting ? (
          submittingLabel
        ) : (
          <>
            <Save className="h-4 w-4" />
            {submitLabel}
          </>
        )}
      </Button>
    </div>
  );
}

